import {IDBPDatabase, openDB} from "idb";
import {ErrorLocationType} from "@/utilities/enums/Enums";
import {GeneralOldResponse} from "@/models/api/old/responses";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";

export class IndexedDbService {
    private readonly dbName:string;
    private readonly storeName:string;
    private readonly keyPropertyName:string;
    private readonly dbVersion:number;
    private dbConnection: Promise<IDBPDatabase<any>>|null;

    constructor(keyPropertyName:string = 'id', name:string="dlt_UploadSessionDb", store:string="sessions", version:number=1) {
        this.dbName = name;
        this.storeName = store;
        this.keyPropertyName = keyPropertyName;
        this.dbVersion = version;
        this.dbConnection = null;
    }

    public async createOrUpdateItem(item:any, errorLocationType:ErrorLocationType):Promise<boolean> {
        try{
            const db = await this.getDb();
            await db.put(this.storeName, item);
        }
        catch(err){
            await ErrorHandler.handleOldError(new GeneralOldResponse(false, `createOrUpdateItem: addOrUpdate failed (${err})`, -1), errorLocationType);
            return false;
        }
        return true;
    }

    public async getItem<T= unknown>(id:string, errorLocationType:ErrorLocationType):Promise<T|null> {
        try {
            const db = await this.getDb();
            return (await db.get(this.storeName, id)) as T;
        }
        catch (e) {
            await ErrorHandler.handleOldError(new GeneralOldResponse(false, `getItem: item not found; ${e}`, -1), errorLocationType);
            return null;
        }
    }

    public async listItems<T= unknown>(errorLocationType:ErrorLocationType):Promise<Array<T>|null> {
        try {
            const db = await this.getDb();
            return (await db.getAll(this.storeName)) as Array<T>;
        }
        catch (e) {
            await ErrorHandler.handleOldError(new GeneralOldResponse(false, `listItems: table not found; ${e}`, -1), errorLocationType);
            return null;
        }
    }

    public async deleteItem(id:string, errorLocationType:ErrorLocationType):Promise<boolean|null> {
        try{
            const db = await this.getDb();
            const transaction = db.transaction(this.storeName, "readwrite");
            const store = transaction.objectStore(this.storeName);
            const elementKey = await store.getKey(id);

            if(!elementKey) {
                await ErrorHandler.handleOldError(new GeneralOldResponse(false, `deleteItem: item not found`, -1), errorLocationType);
                return null;
            }

            await store.delete(elementKey);
        }
        catch(err){
            await ErrorHandler.handleOldError(new GeneralOldResponse(false, `deleteItem: encountered error; ${err}`, -1), errorLocationType);
            return false;
        }
        return true;
    }

    private getDb():Promise<IDBPDatabase> {
        if (this.dbConnection) return this.dbConnection;

        this.dbConnection = openDB(this.dbName, this.dbVersion, {
            upgrade: (db:any) => {
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, {
                        keyPath: this.keyPropertyName,
                    })
                }
            }
        });

        return this.dbConnection;
    }
}